import { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { FormControl } from '@chakra-ui/react'
import { extend, pick } from 'lodash'

import { BotMDDataType, BotMDDropdownFieldBase } from '~shared/types/field'

import { createBaseValidationRules } from '~utils/fieldValidation'
import { SingleSelect } from '~components/Dropdown'
import FormErrorMessage from '~components/FormControl/FormErrorMessage'
import FormLabel from '~components/FormControl/FormLabel'
import Input from '~components/Input'
import Textarea from '~components/Textarea'
import Toggle from '~components/Toggle'

import { CreatePageDrawerContentContainer } from '../../../../../common'
import { FormFieldDrawerActions } from '../common/FormFieldDrawerActions'
import { EditFieldProps } from '../common/types'
import { useEditFieldForm } from '../common/useEditFieldForm'

type EditBotMDDropdownProps = EditFieldProps<BotMDDropdownFieldBase>

const EDIT_BOTMD_DROPDOWN_FIELD_KEYS = [
  'title',
  'description',
  'required',
  'dataType',
] as const

type EditBotMDDropdownKeys = typeof EDIT_BOTMD_DROPDOWN_FIELD_KEYS[number]

type EditBotMDDropdownInputs = Pick<
  BotMDDropdownFieldBase,
  EditBotMDDropdownKeys
>

export const EditBotMDDropdown = ({
  field,
}: EditBotMDDropdownProps): JSX.Element => {
  const {
    register,
    control,
    formState: { errors },
    buttonText,
    handleUpdateField,
    isLoading,
    handleCancel,
  } = useEditFieldForm<EditBotMDDropdownInputs, BotMDDropdownFieldBase>({
    field,
    transform: {
      input: (inputField) => pick(inputField, EDIT_BOTMD_DROPDOWN_FIELD_KEYS),
      output: (formOutput, originalField) =>
        extend({}, originalField, formOutput),
    },
    mode: 'onBlur',
  })

  const requiredValidationRule = useMemo(
    () => createBaseValidationRules({ required: true }),
    [],
  )

  return (
    <CreatePageDrawerContentContainer>
      <FormControl isRequired isReadOnly={isLoading} isInvalid={!!errors.title}>
        <FormLabel>Question</FormLabel>
        <Input autoFocus {...register('title', requiredValidationRule)} />
        <FormErrorMessage>{errors?.title?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isReadOnly={isLoading} isInvalid={!!errors.description}>
        <FormLabel>Description</FormLabel>
        <Textarea {...register('description')} />
        <FormErrorMessage>{errors?.description?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isReadOnly={isLoading}>
        <Toggle {...register('required')} label="Required" />
      </FormControl>
      <FormControl isReadOnly={isLoading} id="dataType">
        <FormLabel isRequired>Data Type</FormLabel>
        <Controller
          control={control}
          name="dataType"
          render={({ field: { value, ...field } }) => (
            <SingleSelect
              isClearable={false}
              items={Object.values(BotMDDataType)}
              value={value ? value : ''}
              {...field}
            />
          )}
        />
      </FormControl>
      <FormFieldDrawerActions
        isLoading={isLoading}
        buttonText={buttonText}
        handleClick={handleUpdateField}
        handleCancel={handleCancel}
      />
    </CreatePageDrawerContentContainer>
  )
}
